import { useReactiveVar } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import GatheringAccessEnum from "src/api/graphql/__generated__/enums/GatheringAccessEnum";
import { uploadDownloadToastFocusedVar } from "src/api/graphql/variables";
import { BottomPopInMenuRecommendedHorizontalPadding } from "src/components/BottomPopInMenu/BottomPopInMenu";
import FooterGradient from "src/components/FooterGradient";
import { RoundedButtonCircleLargeSize } from "src/components/RoundedButton/constants/RoundedButtonCircleLargeConstants";
import { useGatheringStore } from "src/contexts/GatheringContext";
import { useClientId } from "src/helpers/globalHelpers";
import useDevice from "src/hooks/useDevice";
import useDim from "src/hooks/useDim";
import useStateRef from "src/hooks/useStateRef";
import { Timeout } from "src/types/time";
import FloatingFilterMenu from "./components/FloatingFilterMenu";
import FloatingSelect from "./components/FloatingSelect";
import FloatingUpload from "./components/FloatingUpload";
import { GatheringFloatingThemeMenu } from "./components/GatheringFloatingThemeMenu";

const HorizontalPadding = BottomPopInMenuRecommendedHorizontalPadding;
const GatheringHomeFooter = () => {
	const access = useGatheringStore((state) => state.access);
	const isSelecting = useGatheringStore((state) => state.selectMode === "Select");
	const uploadDownloadToastOpen = useReactiveVar(uploadDownloadToastFocusedVar);
	const isEditing = useGatheringStore((state) => state.isEditing);
	// Slight delay on menu entry so that top photos might be pre-populated
	const [isFilterMenuActive, setIsFilterMenuActive] = useState(false);

	const viewMode = useGatheringStore((state) => state.viewMode);
	const hasTopPhotos = useGatheringStore((state) => state.hasTopPhotos);
	const hasPhotos = useGatheringStore((state) => state.hasPhotos);
	const timeoutRef = useRef<Timeout | null>(null);
	useEffect(() => {
		if (hasTopPhotos != null && hasPhotos != null) {
			setIsFilterMenuActive(true);
		} else {
			timeoutRef.current = setTimeout(() => {
				setIsFilterMenuActive(true);
			}, 500);
		}
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [hasTopPhotos, hasPhotos]);
	const clientId = useClientId();
	const filterOtherThanSelfApplied = useGatheringStore(
		(state) =>
			state.filterPhotoVariant.length > 0 ||
			state.filterUploaderIds.length > 0 ||
			state.filterUserIds.some((userId) => userId !== clientId) ||
			state.filterLabels.length > 0 ||
			state.filterRestrictedLabels.length > 0,
	);

	const dim = useDim();
	const { device } = useDevice();
	const didShowThemePicker = useRef(false);
	const { showUpload, showThemePicker, showFilterMenu } = useMemo<{
		showUpload: boolean;
		showThemePicker: boolean;
		showFilterMenu: boolean;
	}>(() => {
		const showUpload =
			!isSelecting &&
			!uploadDownloadToastOpen &&
			!!hasPhotos &&
			device !== "desktop" &&
			!filterOtherThanSelfApplied;
		const showThemePicker = isEditing;
		if (showThemePicker && !didShowThemePicker.current) {
			didShowThemePicker.current = true;
		}
		const showFilterMenu =
			!isSelecting &&
			!uploadDownloadToastOpen &&
			!showThemePicker &&
			// Even if we think there's no photos, we need to show the filter menu so that the user can escape
			(!!hasPhotos || viewMode === "Top" || viewMode === "Filter") &&
			!filterOtherThanSelfApplied &&
			viewMode !== "AllCaller" &&
			viewMode !== "Hidden";

		return {
			showUpload,
			showFilterMenu,
			showThemePicker,
		};
	}, [
		device,
		hasPhotos,
		isEditing,
		filterOtherThanSelfApplied,
		isSelecting,
		uploadDownloadToastOpen,
		viewMode,
	]);
	const [didShowUpload, setDidShowUpload, didShowUploadRef] = useStateRef(showUpload);
	useEffect(() => {
		if (!didShowUploadRef.current && showUpload) {
			setDidShowUpload(true);
		}
	}, [didShowUploadRef, setDidShowUpload, showUpload]);

	const { floatingMenuStyle, floatingUploadExtraBottom } = useMemo<{
		floatingMenuStyle?: StyleProp<ViewStyle>;
		floatingUploadExtraBottom?: number;
	}>(() => {
		// Theme picker just takes up more horizontal space and vertical
		const widthShift = showThemePicker ? 120 : 0;
		const heightShift = showThemePicker ? 20 : 0;

		// Using didShowUpload to avoid weird layout shifts when hiding
		// Floating upload has to go on top of floating menu
		if (dim.width - widthShift < 370 && didShowUpload) {
			return {
				floatingUploadStyle: {
					alignSelf: "flex-end",
					paddingRight: HorizontalPadding,
				},
				floatingUploadExtraBottom: 80 + heightShift,
			};
		}
		// Floating menu and upload are side by side, and equidistant from the sides
		if (dim.width - widthShift < 450 && didShowUpload) {
			return {
				floatingMenuStyle: {
					alignSelf: "flex-end",
					paddingRight:
						Math.min(HorizontalPadding, 0.03 * dim.width) +
						RoundedButtonCircleLargeSize +
						HorizontalPadding,
				},
				floatingUploadStyle: {
					alignSelf: "flex-end",
					paddingRight: HorizontalPadding,
				},
			};
		}
		// Floating menu centered, upload right aligned
		return {
			floatingUploadStyle: {
				alignSelf: "flex-end",
				paddingRight: HorizontalPadding,
			},
		};
	}, [didShowUpload, dim.width, showThemePicker]);

	return (
		<View
			style={{
				position: "absolute",
				width: "100%",
				bottom: 0,
			}}
			pointerEvents="box-none"
		>
			{hasPhotos && <FooterGradient />}
			<FloatingSelect show={isSelecting && !uploadDownloadToastOpen} />
			{access === GatheringAccessEnum.Admin &&
				(showThemePicker || didShowThemePicker.current) && (
					<GatheringFloatingThemeMenu show={showThemePicker} />
				)}
			{isFilterMenuActive && (
				<FloatingFilterMenu show={showFilterMenu} menuStyle={floatingMenuStyle} />
			)}
			{
				// Upload should never show on desktop so we can just remove it completely
				device !== "desktop" && (
					<FloatingUpload
						show={showUpload}
						menuStyle={floatingUploadStyle}
						extraBottom={floatingUploadExtraBottom}
					/>
				)
			}
		</View>
	);
};

const floatingUploadStyle: ViewStyle = {
	alignSelf: "flex-end",
	paddingRight: HorizontalPadding,
};

export default GatheringHomeFooter;
